/* eslint-disable no-param-reassign */
import EventTarget from '@ungap/event-target';
import {
  getOAuthProviderName,
  isOAuthProvider,
  OAuthProvider,
} from '../enums/OAuthProvider';
import { SignInData } from './Auth/types';
import { trackSignIn } from './Auth/helper';
import { UserSchema } from '../definitions/schemas';
import AuthInterface from '../graphql/Auth/AuthInterface';
import AuthTokenProvider from './Auth/AuthTokenProvider';
import UserInterface from '../graphql/User/UserInterface';

export enum AuthEvents {
  BeforeSignOut = 'beforeSignOut',
}

export enum AuthProvider {
  EmailPass = 'email-password',
  Unknown = 'unknown',
}

export enum AuthError {
  NOT_LOGGED_IN = 'Not logged in',
}

export type OTTProvider = AuthProvider | OAuthProvider;

// Implement authentication events
// @see https://medium.com/@zandaqo/eventtarget-the-future-of-javascript-event-systems-205ae32f5e6b
const eventManager = new EventTarget();

/**
 * Flag to avoid falling into a loop of sign outs if several requests happen to
 * fail at the same time with a 401 error, for example, if a session
 * expires or has been invalidated.
 */
let signOutHandled = false;

class AuthService {
  public static getUser(
    onSuccess: (user: UserSchema) => void,
    onFailure?: (error: any) => void,
    tag?: string
  ) {
    const userDataPromise: Promise<UserSchema> = AuthService.isAuthenticated()
      ? new UserInterface().getMe()
      : Promise.reject(AuthError.NOT_LOGGED_IN);

    userDataPromise.then(onSuccess).catch((error) => {
      if (tag) {
        console.warn(`${tag} can't get user: ${error}`);
      }
      if (onFailure) {
        onFailure(error);
      }
    });
  }

  public static isAuthenticated() {
    return AuthTokenProvider.isLoggedIn();
  }

  /**
   * Exchanges the given one time token (OTT) for the user's credentials by
   * letting the api to obtain the user's profile in the target provider. It
   * is expected to obtain an access token that will allow the app to
   * treat the user as being logged in.
   */
  public static async exchangeOTT(
    ott: string,
    ottProvider: OTTProvider,
    referrer?: string
  ): Promise<SignInData> {
    return new Promise<SignInData>(
      // eslint-disable-next-line no-async-promise-executor
      async (resolve: (data: SignInData) => void, reject: () => void) => {
        let forceOverwrite = false;
        if (AuthTokenProvider.isLoggedIn()) {
          // if OTT is provided and there is a logged user,
          // sign out to clear the session and the localstorage to login as the new token
          // don't redirect on signOut, let the flow continue
          await AuthService.signOut({}, true);
          forceOverwrite = true;
        }
        new AuthInterface()
          .signIn({ ott })
          .then((signInData) => {
            const authentication = isOAuthProvider(ottProvider)
              ? getOAuthProviderName(ottProvider as OAuthProvider).toLowerCase()
              : ottProvider;

            AuthTokenProvider.signIn(signInData, forceOverwrite);
            trackSignIn(authentication, referrer);

            resolve(signInData);
          })
          .catch(reject);
      }
    );
  }

  public static on(
    eventName: AuthEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.addEventListener(eventName, callback);
  }

  public static off(
    eventName: AuthEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.removeEventListener(eventName, callback);
  }

  /**
   * @param isSoftSignout - whether it's a signout that doesn't require
   * navigating out of the admin side to take users back to the login screen.
   * This is only used when used with embedded mode inside Mindtickle, as users
   * there log in automatically with an exchanged OTT
   */
  public static async signOut(query = {}, isSoftSignout = false) {
    if (signOutHandled) {
      return;
    }

    signOutHandled = true;
    eventManager.dispatchEvent(new CustomEvent(AuthEvents.BeforeSignOut));

    try {
      await new AuthInterface().signOut();
      if (!isSoftSignout) {
        AuthTokenProvider.signOut(query);
      }
    } catch (e) {
      // this catch is just to handle the promise rejection
    }
  }
}

export default AuthService;
